import { FilterTypeProps } from "../table-filter";
import { addDays, format, isValid, parse, subDays } from "date-fns";

import { IconType } from "react-icons";
import {
  RiCalendarLine,
  RiCheckboxMultipleLine,
  RiContrast2Line,
  RiFlag2Line,
  RiHashtag,
  RiKey2Line,
  RiMapPinLine,
  RiMoneyDollarCircleLine,
  RiServiceLine,
  RiSortAsc,
  RiTextSnippet,
  RiUserLine,
} from "react-icons/ri";

import { FilterTypeConfig } from "./types";

/**
 * Get icon for a filter type
 */
export const getFilterIcon = (
  type: FilterTypeProps["type"],
  filterKey?: string,
): IconType => {
  // Check for specific filter keys that need custom icons
  if (filterKey) {
    const keyLower = filterKey.toLowerCase();
    if (
      keyLower.includes("provider") ||
      keyLower.includes("creator") ||
      keyLower.includes("assignee") ||
      keyLower.includes("user") ||
      keyLower.includes("patient")
    ) {
      return RiUserLine;
    }
    if (keyLower.includes("service")) {
      return RiServiceLine;
    }
    if (keyLower.includes("status") || keyLower.includes("statuses")) {
      return RiFlag2Line;
    }
    if (keyLower.includes("location")) {
      return RiMapPinLine;
    }
  }

  switch (type) {
    case "date":
    case "date-select":
    case "birthday":
      return RiCalendarLine;
    case "number":
      return RiHashtag;
    case "dollar":
      return RiMoneyDollarCircleLine;
    case "sort":
      return RiSortAsc;
    case "text":
      return RiTextSnippet;
    case "id":
      return RiKey2Line;
    case "provider":
      return RiUserLine;
    case "location":
      return RiMapPinLine;
    case "boolean":
      return RiContrast2Line;
    case "multiselect":
    default:
      return RiCheckboxMultipleLine;
  }
};

/**
 * Whether a filter holds no predicate. Empty strings and empty arrays are the
 * shapes controls produce when a value is removed rather than never set.
 */
export const isEmptyFilterValue = (value: any): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * Get indicator symbol for a filter type (used in search results)
 */
export const getFilterIndicator = (
  filterType: FilterTypeConfig,
  isComparison?: boolean,
  isFuzzy?: boolean,
): string => {
  // If filter explicitly defines an indicator, use it
  if (filterType.indicator) {
    return filterType.indicator;
  }
  // Comparisons (number/dollar with =, ≥, ≤) don't need separator
  if (isComparison) {
    return "";
  }
  // Fuzzy/text search uses ~
  if (isFuzzy) {
    return "~";
  }
  // Default based on type
  switch (filterType.type) {
    case "text":
    case "phoneNumber":
    case "id":
      return "~";
    case "number":
    case "dollar":
      return ""; // Comparison symbol is in the label
    default:
      return "›";
  }
};

/**
 * Format a date as --MM-DD (zero-padded month-day, ignoring year)
 */
export const formatMonthDay = (date: Date): string => {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `--${m}-${d}`;
};

type DateFormatter = (date: Date) => string;

const buildPresetRange = (
  days: number,
  direction: "past" | "future",
  fmt: DateFormatter,
): [string, string] => {
  const today = new Date();
  const todayStr = fmt(today);

  if (direction === "future") {
    if (days === 0) return [todayStr, todayStr];
    if (days === 1) {
      const tomorrow = addDays(today, 1);
      return [fmt(tomorrow), fmt(tomorrow)];
    }
    return [todayStr, fmt(addDays(today, days - 1))];
  }

  if (days === 0) return [todayStr, todayStr];
  if (days === 1) {
    const yesterday = subDays(today, 1);
    return [fmt(yesterday), fmt(yesterday)];
  }
  return [fmt(subDays(today, days - 1)), todayStr];
};

/**
 * Create a yyyy-MM-dd range from preset days.
 */
export const createDateFilterValue = (
  days: number,
  direction: "past" | "future" = "past",
): [string, string] =>
  buildPresetRange(days, direction, (d) => format(d, "yyyy-MM-dd"));

/**
 * Create a --MM-DD (year-agnostic) range from preset days. Used by the
 * birthday filter type.
 */
export const createBirthdayFilterValue = (
  days: number,
  direction: "past" | "future" = "past",
): [string, string] => buildPresetRange(days, direction, formatMonthDay);

/**
 * Day name mappings for date parsing
 */
const DAY_NAMES: Record<string, number> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

/**
 * Common date formats to try when parsing
 */
const DATE_FORMATS = [
  "MM/dd/yyyy",
  "MM/dd/yy",
  "MM/dd",
  "M/d/yyyy",
  "M/d/yy",
  "M/d",
  "yyyy-MM-dd",
  "MMM d, yyyy",
  "MMM d yyyy",
  "MMM d",
  "MMMM d, yyyy",
  "MMMM d yyyy",
  "MMMM d",
  "d MMM yyyy",
  "d MMM",
  "d MMMM yyyy",
  "d MMMM",
  "MMM",
];

/**
 * Try parsing a search query as a specific date
 */
export const tryParseDate = (query: string): Date | null => {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const today = new Date();

  // Check for day names first (returns most recent occurrence of that day)
  const lowerTrimmed = trimmed.toLowerCase();
  if (DAY_NAMES[lowerTrimmed] !== undefined) {
    const targetDay = DAY_NAMES[lowerTrimmed];
    const currentDay = today.getDay();
    // Calculate days ago (if today is the target day, return today)
    let daysAgo = currentDay - targetDay;
    if (daysAgo < 0) daysAgo += 7;
    if (daysAgo === 0) daysAgo = 0; // Today
    const result = subDays(today, daysAgo);
    return result;
  }

  // Try common date formats
  for (const fmt of DATE_FORMATS) {
    try {
      const parsed = parse(trimmed, fmt, today);
      if (isValid(parsed)) {
        // If no year was in the format, the parse function uses the reference date's year
        return parsed;
      }
    } catch {
      // Continue to next format
    }
  }

  return null;
};

/**
 * Date-related terms for query detection
 */
export const DATE_TERMS = [
  "date",
  "day",
  "week",
  "month",
  "year",
  "ago",
  "last",
  "next",
  "today",
  "yesterday",
  "tomorrow",
  // Month names (full and abbreviated)
  "january",
  "jan",
  "february",
  "feb",
  "march",
  "mar",
  "april",
  "apr",
  "may",
  "june",
  "jun",
  "july",
  "jul",
  "august",
  "aug",
  "september",
  "sep",
  "sept",
  "october",
  "oct",
  "november",
  "nov",
  "december",
  "dec",
];

/**
 * Date terms used for scoring (to detect if query looks like a date)
 */
export const DATE_TERMS_FOR_SCORING = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "january",
  "february",
  "march",
  "april",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
  "today",
  "yesterday",
  "tomorrow",
  "next",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];
